import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { download } from "@/lib/download";
import { formatIsoDay, formatTRY } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import ProjectDialog from "@/components/insaat/ProjectDialog";
import ExpenseDialog from "@/components/insaat/ExpenseDialog";
import ExpenseViewDialog from "@/components/insaat/ExpenseViewDialog";
import { ExpenseFilters, EMPTY_FILTERS } from "@/components/insaat/ExpenseFilters";
import { ExpenseTable } from "@/components/insaat/ExpenseTable";
import { CategorySummary } from "@/components/insaat/CategorySummary";
import { StatusBadge } from "@/components/insaat/common";
import InstrumentPayments from "@/components/insaat/InstrumentPayments";

const Card = ({ label, value, testid, small }) => (
  <div className={`panel min-w-0 ${small ? "p-3" : "p-4"}`}><div className="text-[11px] uppercase tracking-wider text-slate-500 truncate">{label}</div>
    <div className={`mt-1 font-mono font-semibold text-slate-900 break-all ${small ? "text-sm md:text-base" : "text-base sm:text-lg md:text-xl"}`} data-testid={testid}>{value}</div></div>
);

export default function ConstructionDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [p, setP] = useState(null);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [res, setRes] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("giderler");
  const seeExp = can("insaat_gider");
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
  const loadP = useCallback(() => api.get(`/insaat/projects/${id}`).then(({ data }) => setP(data)).catch((e) => toast.error(apiError(e))), [id]);
  const loadE = useCallback(() => { if (seeExp) api.get(`/insaat/projects/${id}/expenses`, { params: { ...params, page_size: 500 } }).then(({ data }) => setRes(data)).catch((e) => toast.error(apiError(e))); },
    [id, seeExp, JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadP(); api.get("/insaat/meta").then(({ data }) => setMeta(data)); }, [loadP]);
  useEffect(() => { loadE(); }, [loadE]);
  useEffect(() => {
    const g = sp.get("gider");
    if (g && seeExp) api.get(`/insaat/expenses/${g}`).then(({ data }) => setDlg({ kind: "view", e: data })).catch((e) => toast.error(apiError(e)));
  }, [sp, seeExp]);
  const refresh = () => { loadP(); loadE(); };
  const close = () => { setDlg(null); if (sp.get("gider")) setSp({}); };
  const exportXlsx = async () => {
    setBusy(true);
    try { await download(`/insaat/projects/${id}/expenses.xlsx`, params, "insaat_giderleri.xlsx"); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const remove = async (e, reason) => {
    try { await api.delete(`/insaat/expenses/${e.id}`, { data: { reason } }); toast.success(`${e.no} silindi; finans kaydı ters çevrildi`); setDlg(null); refresh(); }
    catch (er) { toast.error(apiError(er)); }
  };
  const removeProject = async () => {
    const reason = window.prompt(`"${p.name}" silinsin mi? (Yalnız aktif gideri olmayan inşaat silinebilir) Neden:`, "");
    if (reason === null) return;
    try { await api.delete(`/insaat/projects/${id}`, { data: { reason } }); toast.success("İnşaat silindi"); nav("/insaat"); } catch (e) { toast.error(apiError(e)); }
  };
  if (!p) return <div className="panel h-40 animate-pulse" data-testid="project-loading" />;
  const s = p.summary;
  return (
    <div className="space-y-4 min-w-0" data-testid="project-detail-page">
      <Link to="/insaat" className="inline-flex items-center gap-1 text-[13px] text-slate-500 hover:text-slate-900" data-testid="project-back"><ArrowLeft className="h-4 w-4" />İnşaatlar</Link>
      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2"><h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 break-words" data-testid="project-title">{p.name}</h1><StatusBadge status={p.status} testid="project-status" /></div>
          <div className="text-[13px] text-slate-500 mt-1 flex flex-wrap gap-x-4"><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.location || "—"}</span><span>Başlangıç: {formatIsoDay(p.start_date)}</span></div>
          {p.description && <p className="text-[13px] text-slate-600 mt-1 max-w-3xl">{p.description}</p>}
        </div>
        <div className="md:ml-auto flex flex-wrap gap-2">
          {can("insaat", "create") && <Button variant="outline" onClick={() => setDlg({ kind: "project" })} data-testid="project-edit-button"><Pencil className="h-4 w-4 mr-1" />Düzenle</Button>}
          {can("insaat", "create") && <Button variant="outline" className="text-rose-700" onClick={removeProject} data-testid="project-delete-button"><Trash2 className="h-4 w-4 mr-1" />Sil</Button>}
          {seeExp && <Button variant="outline" disabled={busy} onClick={exportXlsx} data-testid="expense-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1 text-emerald-700" />{busy ? "Hazırlanıyor…" : "Excel"}</Button>}
          {can("insaat_gider", "create") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setDlg({ kind: "expense" })} data-testid="expense-new-button"><Plus className="h-4 w-4 mr-1" />Yeni Gider Ekle</Button>}
        </div>
      </div>
      {s && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Toplam Gider" value={formatTRY(s.total_try)} testid="summary-total" />
        <Card label="Bu Ayki Gider" value={formatTRY(s.month_try)} testid="summary-month" />
        <Card label="Toplam Gider Kaydı" value={s.expense_count} testid="summary-count" />
        <Card label="Son Gider Tarihi" value={formatIsoDay(s.last_expense_date)} testid="summary-last" />
        <Card small label="Malzeme" value={formatTRY(s.type_totals?.malzeme)} testid="summary-type-malzeme" />
        <Card small label="İşçilik" value={formatTRY(s.type_totals?.iscilik)} testid="summary-type-iscilik" />
        <Card small label="Hizmet" value={formatTRY(s.type_totals?.hizmet)} testid="summary-type-hizmet" />
        {s.type_totals?.belirtilmemis > 0 && <Card small label="Belirtilmemiş" value={formatTRY(s.type_totals.belirtilmemis)} testid="summary-type-belirtilmemis" />}
      </div>}
      {seeExp && can("cek_senet") && <div className="flex gap-1" data-testid="project-tabs">
        {[["giderler", "Giderler"], ["cek", "Çek/Senet ile Ödemeler"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`rounded px-3 py-1.5 text-[13px] ${tab === k ? "bg-slate-900 text-white" : "bg-slate-100"}`} data-testid={`project-tab-${k}`}>{l}</button>))}
      </div>}
      {tab === "cek" && seeExp && can("cek_senet") ? <InstrumentPayments projectId={id} /> : seeExp ? (<>
        <ExpenseFilters value={filters} onChange={setFilters} />
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px] items-start min-w-0">
          <ExpenseTable res={res} onView={(e) => setDlg({ kind: "view", e })} onDocs={(e) => setDlg({ kind: "docs", e })} onEdit={(e) => setDlg({ kind: "expense", e })} onDelete={remove} />
          <CategorySummary res={res} />
        </div>
      </>) : <div className="panel p-6 text-sm text-slate-500" data-testid="expense-no-permission">Gider görüntüleme yetkiniz yok.</div>}
      {dlg?.kind === "project" && <ProjectDialog project={p} onClose={() => setDlg(null)} onDone={() => { setDlg(null); loadP(); }} />}
      {dlg?.kind === "expense" && <ExpenseDialog projectId={id} expense={dlg.e} meta={meta} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {(dlg?.kind === "view" || dlg?.kind === "docs") && <ExpenseViewDialog expense={dlg.e} docsOnly={dlg.kind === "docs"} onClose={close} onChanged={loadE} />}
    </div>
  );
}
